import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X, Copy } from 'lucide-react';
import {
  Condition,
  OperatorType,
  FilterType,
  getOperatorMetadata,
  getOperatorsForField,
  getFilterTypeFromField,
  FILTER_TYPES,
} from '@/types/filter';

// Gmail category options
const GMAIL_CATEGORIES = [
  { value: 'social', label: 'Social' },
  { value: 'promotions', label: 'Promotions' },
  { value: 'forums', label: 'Forums' },
  { value: 'updates', label: 'Updates' },
  { value: 'primary', label: 'Primary' },
] as const;

// Gmail status options
const GMAIL_STATUSES = [
  { value: 'INBOX', label: 'INBOX' },
  { value: 'UNREAD', label: 'UNREAD' },
  { value: 'STARRED', label: 'STARRED' },
  { value: 'IMPORTANT', label: 'IMPORTANT' },
  { value: 'SPAM', label: 'SPAM' },
  { value: 'TRASH', label: 'TRASH' },
  { value: 'SENT', label: 'SENT' },
  { value: 'DRAFT', label: 'DRAFT' },
  { value: 'SCHEDULED', label: 'SCHEDULED' },
  { value: 'MUTED', label: 'MUTED' },
  { value: 'CHAT', label: 'CHAT' },
  { value: 'UNSENT', label: 'UNSENT' },
] as const;

interface ConditionBuilderProps {
  condition: Condition;
  onChange: (condition: Condition) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  tabIndexStart?: number;
}

export function ConditionBuilder({
  condition,
  onChange,
  onRemove,
  onDuplicate,
  tabIndexStart,
}: ConditionBuilderProps) {
  const filterType = getFilterTypeFromField(condition.field);
  const availableOperators = getOperatorsForField(condition.field);
  const operatorMetadata = getOperatorMetadata(condition.operator);

  // Check if this is a sender_history count operator that should be displayed as a sentence
  const isCountOperator = filterType === 'sender_history' &&
    (condition.operator === 'sent_count_greater_than' || condition.operator === 'sent_count_less_than');

  const handleFieldChange = (newField: string) => {
    const newFieldType = getFilterTypeFromField(newField);
    const newOperators = getOperatorsForField(newField);
    const defaultOperator = newOperators[0] || 'contains';

    onChange({
      ...condition,
      field: newField,
      operator: defaultOperator,
      value: '',
      caseSensitive: false,
      timeWindowDays: undefined,
    });
  };

  const handleOperatorChange = (operator: OperatorType) => {
    const newOperatorMeta = getOperatorMetadata(operator);
    onChange({
      ...condition,
      operator,
      // Clear value if new operator doesn't require it
      value: newOperatorMeta.requiresValue ? condition.value : '',
      // Clear time window if not needed
      timeWindowDays: newOperatorMeta.requiresTimeWindow ? condition.timeWindowDays || 30 : undefined,
    });
  };

  return (
    <div className="flex items-start gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900">
      <div className="flex-1 space-y-3">
        {/* Field Type Selection */}
        <div className="flex-1">
          <Select
            value={condition.field}
            onValueChange={handleFieldChange}
          >
            <SelectTrigger id={`field-${condition.id}`} tabIndex={tabIndexStart}>
              <SelectValue placeholder="Select field" />
            </SelectTrigger>
            <SelectContent>
              {FILTER_TYPES.map((ft) => (
                <SelectItem key={ft.field} value={ft.field}>
                  {ft.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isCountOperator ? (
          /* Sentence-like layout for count operators */
          <div className="flex flex-wrap items-center gap-2">
            {/* Operator Selection */}
            <Select
              value={condition.operator}
              onValueChange={handleOperatorChange}
            >
              <SelectTrigger id={`operator-${condition.id}`} className="w-auto" tabIndex={tabIndexStart}>
                <SelectValue placeholder="Select operator" />
              </SelectTrigger>
              <SelectContent>
                {availableOperators.map((op) => {
                  const meta = getOperatorMetadata(op);
                  return (
                    <SelectItem key={op} value={op}>
                      {meta.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {/* Value Input */}
            <Input
              id={`value-${condition.id}`}
              type="number"
              min="0"
              value={condition.value}
              onChange={(e) => onChange({ ...condition, value: e.target.value })}
              placeholder="0"
              className="w-20"
              tabIndex={tabIndexStart !== undefined ? tabIndexStart + 1 : undefined}
            />

            <span className="text-sm text-gray-700 dark:text-gray-300">emails in the past</span>

            {/* Time Window Input */}
            <Input
              id={`time-window-${condition.id}`}
              type="number"
              min="1"
              value={condition.timeWindowDays || 30}
              onChange={(e) =>
                onChange({
                  ...condition,
                  timeWindowDays: parseInt(e.target.value) || 30,
                })
              }
              placeholder="30"
              className="w-20"
              tabIndex={tabIndexStart !== undefined ? tabIndexStart + 2 : undefined}
            />

            <span className="text-sm text-gray-700 dark:text-gray-300">days</span>
          </div>
        ) : (
          <>
            {/* Operator and Value - Inline on md+, stacked on smaller screens */}
            <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-2">
              {/* Operator Selection */}
              <div className="flex-1 md:flex-initial md:w-auto">
                <Select
                  value={condition.operator}
                  onValueChange={handleOperatorChange}
                >
                  <SelectTrigger id={`operator-${condition.id}`} tabIndex={tabIndexStart}>
                    <SelectValue placeholder="Select operator" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableOperators.map((op) => {
                      const meta = getOperatorMetadata(op);
                      return (
                        <SelectItem key={op} value={op}>
                          {meta.label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Value Input */}
              {operatorMetadata.requiresValue && (
                <div className="flex-1">
                  {filterType === 'gmail_category' ? (
                    <Select
                      value={condition.value}
                      onValueChange={(value) => onChange({ ...condition, value })}
                    >
                      <SelectTrigger
                        id={`value-${condition.id}`}
                        tabIndex={tabIndexStart !== undefined ? tabIndexStart + 1 : undefined}
                      >
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {GMAIL_CATEGORIES.map((category) => (
                          <SelectItem key={category.value} value={category.value}>
                            {category.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : filterType === 'gmail_status' ? (
                    <Select
                      value={condition.value}
                      onValueChange={(value) => onChange({ ...condition, value })}
                    >
                      <SelectTrigger
                        id={`value-${condition.id}`}
                        tabIndex={tabIndexStart !== undefined ? tabIndexStart + 1 : undefined}
                      >
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {GMAIL_STATUSES.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={`value-${condition.id}`}
                      type="text"
                      value={condition.value}
                      onChange={(e) => onChange({ ...condition, value: e.target.value })}
                      placeholder="Enter value"
                      tabIndex={tabIndexStart !== undefined ? tabIndexStart + 1 : undefined}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Time Window (for sender_history count operators) */}
            {operatorMetadata.requiresTimeWindow && (
              <div className="space-y-1.5">
                <Label htmlFor={`time-window-${condition.id}`}>Time Window (days)</Label>
                <Input
                  id={`time-window-${condition.id}`}
                  type="number"
                  min="1"
                  value={condition.timeWindowDays || 30}
                  onChange={(e) =>
                    onChange({
                      ...condition,
                      timeWindowDays: parseInt(e.target.value) || 30,
                    })
                  }
                  placeholder="30"
                />
              </div>
            )}
          </>
        )}

        {/* Case Sensitive Checkbox (only for text operators) */}
        {operatorMetadata.requiresValue &&
          !operatorMetadata.requiresTimeWindow &&
          ['contains', 'does_not_contain', 'equals', 'does_not_equal', 'starts_with', 'ends_with'].includes(
            condition.operator
          ) && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`case-sensitive-${condition.id}`}
                checked={condition.caseSensitive || false}
                onCheckedChange={(checked) =>
                  onChange({
                    ...condition,
                    caseSensitive: checked as boolean,
                  })
                }
                className="border-gray-300 dark:border-gray-600 data-[state=checked]:bg-blue-600 dark:data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 dark:data-[state=checked]:border-blue-600"
              />
              <Label
                htmlFor={`case-sensitive-${condition.id}`}
                className="text-sm font-normal cursor-pointer text-blue-950 dark:text-gray-100"
              >
                Case sensitive
              </Label>
            </div>
          )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-1 shrink-0 mt-6 md:mt-0 md:mb-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          title="Remove condition"
        >
          <X className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onDuplicate}
          title="Duplicate condition"
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
